import { useCallback, useEffect, useRef, useState } from "react";
import { type UseFormSetValue } from "react-hook-form";
import { useDispatch } from "src/redux/hooks";
import { setCellProperty } from "src/redux/slices/generatorSlice";

interface Props {
  id: string;
  enabled: boolean;
  manualEdit?: boolean;
  initialValue?: any;
  prefillValue?: any; // value from before the manual one
  sectionIndex?: number;
  rowIndex?: number;
  setValue: UseFormSetValue<Record<string, any>>;
}

/** Lets a calculated cell hold a manual value: calc hooks write through `calcSetValue`, which is held back while the cell is manual. */
const useManualEdit = ({
  id,
  enabled,
  manualEdit,
  initialValue,
  prefillValue,
  sectionIndex,
  rowIndex,
  setValue,
}: Props) => {
  const dispatch = useDispatch();
  const isManual = enabled && Boolean(manualEdit);
  const isManualRef = useRef(isManual);
  isManualRef.current = isManual;
  const calculated = useRef<{ value: any } | null>(null);
  // A cell saved as manual mounts with the manual value, so its saved prefill is used
  const prefill = useRef({
    value: isManual && prefillValue !== undefined ? prefillValue : initialValue,
  });
  const wasManual = useRef(isManual);
  const [recalcKey, setRecalcKey] = useState(0);

  const calcSetValue = useCallback(
    (name: string, value: any, options?: Record<string, any>) => {
      if (name !== id) {
        setValue(name, value, options);
        return;
      }
      calculated.current = { value };
      if (!isManualRef.current) setValue(name, value, options);
    },
    [id, setValue],
  ) as UseFormSetValue<Record<string, any>>;

  useEffect(() => {
    const returnedToAuto = wasManual.current && !isManual;
    wasManual.current = isManual;
    if (!returnedToAuto) return;

    // Bring back what was calculated meanwhile (or the prefilled value), then let lazy calcs run again
    setValue(id, (calculated.current ?? prefill.current).value);
    setRecalcKey(key => key + 1);
  }, [id, isManual, setValue]);

  const setManualEdit = useCallback(
    (value: boolean) => {
      if (!enabled) return;
      dispatch(
        setCellProperty({
          sectionIndex,
          rowIndex: rowIndex ?? 0,
          cellId: id,
          change: "manualEdit",
          property: value
            ? { manualEdit: true, prefillValue: prefill.current.value }
            : { manualEdit: false },
        }),
      );
    },
    [dispatch, enabled, id, rowIndex, sectionIndex],
  );

  return { calcSetValue, setManualEdit, recalcKey };
};

export default useManualEdit;
